<template>
  <!-- O item como a visão compacta o mostra — a mesma forma da linha de ambiente da tela inicial
       (escolha do usuário): a régua à esquerda, o nome em destaque, e embaixo as outras colunas,
       cada uma com o seu nome. A situação à direita, onde a home põe o nível. -->
  <div class="instance-list-row">
    <ontos-color-accent row :color="instanceColorOf(instance)"/>
    <div class="instance-list-row-text">
      <ontos-instance-identifier :instance="instance" :idea="idea" class="instance-list-row-title text-subtitle-1"/>
      <!-- Cada coluna numa linha, com o nome dela antes (decisão do usuário). Foi primeiro sem os
           nomes, os valores juntos separados por "·", e muitos ficavam ambíguos — duas datas, dois
           números. Os nomes numa coluna só, alinhados; cada valor numa linha, com reticências. Os
           valores na variante do card, a que se lê numa linha: o markdown linearizado, a seleção
           na bolinha. -->
      <div v-if="fields.length" class="instance-list-row-fields text-body-2">
        <template v-for="field of fields" :key="field.property.alias">
          <span class="instance-list-row-label text-caption text-medium-emphasis" :title="field.property.name">
            {{ field.property.name }}
          </span>
          <span class="instance-list-row-value text-medium-emphasis">
            <template v-for="(value, valueIndex) of field.values" :key="`${field.property.alias}.${valueIndex}`">
              <span v-if="valueIndex > 0">, </span>
              <ontos-property-value :property="field.property" :value="value" :item="instance" :context="context"
                                    :choice-domains="choiceDomains" :accent="false" variant="card"/>
            </template>
          </span>
        </template>
      </div>
    </div>
    <!-- A situação no semáforo com o nome, o padrão do app (relato do usuário: no chip, quebrava) -->
    <ontos-colored-value v-if="state" :option="state" form="dot" class="instance-list-row-state"/>
    <!-- O que é de quem hospeda: o desvincular da ficha -->
    <span v-if="$slots.actions" class="instance-list-row-actions"><slot name="actions"/></span>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import Domain from '@/types/Domain'
import {ColoredOption} from '@/types/ColoredOption'
import {InstanceContext} from '@/types/InstanceContext'
import ColorAccent from '@/components/shared/ColorAccent.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import InstanceIdentifier from '@/components/shared/InstanceIdentifier.vue'
import PropertyValue from '@/components/live/instance/PropertyValue.vue'
import {instanceColorOf} from '@/utils/propertyDisplay'
import {isMediaType} from '@/utils/mediaTypes'

/** Uma coluna na linha da visão compacta: a propriedade e os valores dela neste item. */
export type RowField = { property: IdeaProperty, values: any[] }

/**
 * As colunas que vão para a linha de baixo: as que não são o nome (o identificador já é o título) nem
 * mídia (uma imagem não cabe numa linha de texto), e que têm valor neste item. Quem diz os valores é
 * quem hospeda — a grade e a ficha os leem cada uma do seu jeito.
 */
export function rowFieldsOf(properties: IdeaProperty[], valuesOf: (property: IdeaProperty) => any[]): RowField[] {
  return properties
      .filter(property => !property.identifier && !isMediaType(property))
      .map(property => ({property, values: valuesOf(property)}))
      .filter(field => field.values.some(value => value !== undefined && value !== null && value !== ''))
}

/**
 * Um item na visão compacta, no lugar da linha de tabela: a grade da coleção e as tabelas de
 * relacionamento da ficha, quando as colunas não cabem na largura (ver TableFit) — cada linha virava
 * duas ou três de texto apertado, e o resto das colunas só rolando para o lado.
 *
 * O clique é de quem hospeda, e abre o item.
 */
@Component({
  name: 'ontos-instance-list-row',
  components: {
    'ontos-color-accent': ColorAccent,
    'ontos-colored-value': ColoredValue,
    'ontos-instance-identifier': InstanceIdentifier,
    'ontos-property-value': PropertyValue
  }
})
class InstanceListRow extends Vue {
  @Prop({type: Object, required: true}) readonly instance!: any
  @Prop({type: Object as () => Idea, required: true}) readonly idea!: Idea
  @Prop({type: Array as () => RowField[], default: () => []}) readonly fields!: RowField[]
  /** A situação, na coleção com fluxo; sem fluxo, nada. */
  @Prop({type: Object as () => ColoredOption, default: null}) readonly state!: ColoredOption | null
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  @Prop({type: Array as () => Domain[], default: () => []}) readonly choiceDomains!: Domain[]

  //exposta ao template: função de módulo não é enxergada de lá
  instanceColorOf = instanceColorOf
}

export default toNative(InstanceListRow)
</script>

<style scoped>
/* A situação e as ações à direita, só na linha do nome; os campos embaixo na largura inteira — com a
   situação numa coluna da altura do item, eles cortavam no meio da tela, com espaço sobrando (relato
   do usuário; o mesmo arranjo da linha de ambiente da tela inicial). A régua é absoluta (`row`), e
   por isso o `relative` e o recuo à esquerda */
.instance-list-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: start;
  column-gap: 8px;
  padding: 10px 8px 10px 16px;
  cursor: pointer;
}

/* O texto na largura inteira, com as colunas da linha (`subgrid`): o nome na primeira, ao lado da
   situação e das ações, e os campos atravessando todas */
.instance-list-row-text {
  grid-column: 1 / -1;
  grid-row: 1;
  display: grid;
  grid-template-columns: subgrid;
  row-gap: 2px;
  min-width: 0;
}

.instance-list-row-text > * {
  grid-column: 1 / -1;
  min-width: 0;
}

.instance-list-row-text > .instance-list-row-title {
  grid-column: 1;
  font-weight: 500;
  line-height: 1.4;
}

/* Os nomes numa coluna, até 40% da largura, e os valores na que sobra: os de todas as colunas
   começam no mesmo ponto, e um nome longo corta antes de empurrar o valor */
.instance-list-row-fields {
  display: grid;
  grid-template-columns: fit-content(40%) minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 2px;
  margin-top: 2px;
}

.instance-list-row-label,
.instance-list-row-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O nome na linha de base do valor, que é maior */
.instance-list-row-label {
  line-height: 1.43rem;
}

.instance-list-row-state {
  grid-column: 2;
  grid-row: 1;
  margin-top: 2px;
}

.instance-list-row-actions {
  grid-column: 3;
  grid-row: 1;
}
</style>
